import { PageHero } from '@components/common';
import { ArrowRight, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const benefits = [
  'Competitive salary and equity options',
  'Flexible working arrangements',
  'Continuous professional development budget',
  'Work with diverse, interesting clients',
  'Join a network of elite security professionals',
  'Make a real impact on business security',
];

const openRoles = [
  {
    id: 'fractional-ciso',
    title: 'Fractional CISO',
    location: 'Remote UK',
    type: 'Contract',
    description:
      "We're always looking for experienced CISOs with extensive cybersecurity leadership experience to join our network.",
  },
];

const closedRoles = [
  {
    id: 'security-consultant',
    title: 'Security Consultant',
    location: 'Remote UK',
    type: 'Full-time',
    description: 'Support our CISOs in delivering security assessments and compliance programmes.',
  },
  {
    id: 'client-success-manager',
    title: 'Client Success Manager',
    location: 'Remote UK',
    type: 'Full-time',
    description: 'Ensure our clients get maximum value from their fractional CISO engagements.',
  },
];

export default function CareersPage() {
  return (
    <>
      <PageHero
        title="Join Our Elite Network"
        eyebrow="Careers"
        seoTitle="Careers | The CISO Network"
        seoDescription="Join our team of elite cybersecurity professionals. Help businesses build world-class security programs."
      />

      {/* Why Join */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-secondary tracking-wide uppercase">
                Why Join Us
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-3 mb-6">
                Work That Matters
              </h2>
              <p className="text-muted-foreground mb-8">
                At The CISO Network, you'll help businesses protect what matters most. Our
                consultants make a real difference every day.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-background text-center">
                <div className="text-4xl font-heading font-bold text-teal mb-2">Flexible</div>
                <div className="text-sm text-muted-foreground">Working arrangements</div>
              </div>
              <div className="p-6 rounded-xl bg-background text-center">
                <div className="text-4xl font-heading font-bold text-teal mb-2">Remote</div>
                <div className="text-sm text-muted-foreground">First culture</div>
              </div>
              <div className="p-6 rounded-xl bg-background text-center">
                <div className="text-4xl font-heading font-bold text-teal mb-2">Growth</div>
                <div className="text-sm text-muted-foreground">Professional dev budget</div>
              </div>
              <div className="p-6 rounded-xl bg-background text-center">
                <div className="text-4xl font-heading font-bold text-teal mb-2">Impact</div>
                <div className="text-sm text-muted-foreground">Real business results</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-secondary tracking-wide uppercase">
              Open Positions
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-3">
              Join Our Team
            </h2>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto">
            {openRoles.map(job => (
              <div
                key={job.id}
                className="card-custom flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                    {job.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-2">{job.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {job.type}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/careers/${job.id}`}
                  className="btn-secondary-custom text-sm whitespace-nowrap"
                >
                  View Role <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>

          {/* Closed Positions */}
          {closedRoles.length > 0 && (
            <>
              <div className="text-center mt-16 mb-8">
                <span className="text-sm font-medium text-muted-foreground tracking-wide uppercase">
                  Closed Positions
                </span>
              </div>
              <div className="space-y-4 max-w-3xl mx-auto opacity-60">
                {closedRoles.map(job => (
                  <div
                    key={job.id}
                    className="card-custom flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-muted/30"
                  >
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                        {job.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-2">{job.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/careers/${job.id}`}
                      className="px-4 py-2 text-sm font-medium text-muted-foreground whitespace-nowrap border border-border rounded-lg hover:border-teal/50 hover:text-teal transition-colors"
                    >
                      View Role <ArrowRight className="ml-1 h-4 w-4 inline" />
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">
              Don't see a role that fits? We're always interested in meeting talented security
              professionals.
            </p>
            <a href="mailto:careers@thecisonetwork.com" className="btn-outline-custom">
              Send Us Your CV
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
